import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Inbox, FileText, Receipt, CalendarDays, Users,
  UsersRound, BookOpen, Mail, LogOut, X, Home, Menu,
} from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/constants";
import { useAuth } from "@/lib/auth.jsx";
import ChatAssistant from "@/components/crm/ChatAssistant";

const NAV = [
  { to: "/crm/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/crm/anfragen", icon: Inbox, label: "Anfragen" },
  { to: "/crm/angebote", icon: FileText, label: "Angebote" },
  { to: "/crm/rechnungen", icon: Receipt, label: "Rechnungen" },
  { to: "/crm/termine", icon: CalendarDays, label: "Termine" },
  { to: "/crm/kunden", icon: Users, label: "Kunden" },
  { to: "/crm/mitarbeiter", icon: UsersRound, label: "Mitarbeiter" },
  { to: "/crm/wissensbasis", icon: BookOpen, label: "Wissensbasis" },
  { to: "/crm/vorlagen", icon: Mail, label: "Vorlagen" },
];

// Mobile: die wichtigsten Bereiche als Tab-Leiste, der Rest unter "Mehr"
const MOBILE_TABS = ["/crm/dashboard", "/crm/anfragen", "/crm/termine", "/crm/kunden"];
const MOBILE_SHORT = { "/crm/dashboard": "Start" };

export default function CRMLayout() {
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const current = NAV.find((n) => pathname.startsWith(n.to));
  const moreItems = NAV.filter((n) => !MOBILE_TABS.includes(n.to));
  const moreActive = moreItems.some((n) => pathname.startsWith(n.to));

  useEffect(() => { setMoreOpen(false); window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="crm-app min-h-screen flex bg-[#FAFAF5]">
      {/* Sidebar */}
      <aside
        className="hidden md:relative md:flex md:w-64 shrink-0 bg-white border-r border-[#E5E7EB] flex-col"
      >
        <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-auto" />
          <div className="leading-tight">
            <div className="font-display text-sm font-semibold text-[#1A201A]">Blessing CRM</div>
            <div className="text-[10px] text-[#6B7F3E] uppercase tracking-wider">Portal</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`crm-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#F5F6F0] text-[#2C3E2D] border-l-2 border-[#6B7F3E] pl-[10px]"
                      : "text-[#5C6B5D] hover:bg-[#F5F6F0] hover:text-[#2C3E2D]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E5E7EB] space-y-1">
          <a
            href="/"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-[#5C6B5D] hover:bg-[#F5F6F0]"
            data-testid="crm-back-to-site"
          >
            <Home className="w-4 h-4" /> Zur Website
          </a>
          <div className="px-3 py-2 text-xs text-[#5C6B5D]">
            <div className="font-medium text-[#1A201A]">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider">{user?.role}</div>
          </div>
          <button
            onClick={() => { logout(); nav("/login"); }}
            data-testid="crm-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#B85C38] hover:bg-[#FDF2EA]"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile App-Kopfzeile */}
        <header className="md:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E5E7EB] px-4 pt-[env(safe-area-inset-top)]">
          <div className="h-14 flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-8 w-auto" />
            <div className="leading-tight min-w-0">
              <div className="font-display text-base font-semibold text-[#1A201A] truncate">{current?.label || "Blessing CRM"}</div>
              <div className="text-[10px] text-[#6B7F3E] uppercase tracking-wider">Blessing CRM</div>
            </div>
          </div>
        </header>
        <div className="p-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile Tab-Leiste */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5E7EB] pb-[env(safe-area-inset-bottom)]"
        data-testid="crm-mobile-tabbar"
      >
        <div className="grid grid-cols-5 h-16">
          {MOBILE_TABS.map((to) => {
            const item = NAV.find((n) => n.to === to);
            const Icon = item.icon;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`crm-tab-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[11px] font-medium select-none active:scale-95 transition-transform ${isActive ? "text-[#2C3E2D]" : "text-[#8A968B]"}`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`px-4 py-1 rounded-full transition-colors ${isActive ? "bg-[#E8ECE1]" : ""}`}>
                      <Icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
                    </span>
                    {MOBILE_SHORT[to] || item.label}
                  </>
                )}
              </NavLink>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="crm-tab-mehr"
            className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium select-none active:scale-95 transition-transform ${moreActive || moreOpen ? "text-[#2C3E2D]" : "text-[#8A968B]"}`}
          >
            <span className={`px-4 py-1 rounded-full transition-colors ${moreActive || moreOpen ? "bg-[#E8ECE1]" : ""}`}>
              <Menu className="w-5 h-5" strokeWidth={moreActive ? 2 : 1.5} />
            </span>
            Mehr
          </button>
        </div>
      </nav>

      {/* Mobile "Mehr"-Menü als Bottom-Sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="crm-more-sheet">
          <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-200" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1.5 rounded-full bg-[#E5E7EB]" />
            </div>
            <div className="flex items-center justify-between px-5 pt-2 pb-3">
              <div>
                <div className="font-display text-lg font-semibold text-[#1A201A]">Alle Bereiche</div>
                <div className="text-xs text-[#5C6B5D]">
                  {user?.name} · <span className="uppercase tracking-wider text-[10px]">{user?.role}</span>
                </div>
              </div>
              <button onClick={() => setMoreOpen(false)} className="p-2 -mr-2 rounded-full hover:bg-[#F5F6F0]" aria-label="Schließen">
                <X className="w-5 h-5 text-[#5C6B5D]" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 px-5">
              {moreItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`crm-more-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-2 rounded-2xl border py-4 text-xs font-medium active:scale-95 transition-transform ${
                        isActive ? "bg-[#E8ECE1] border-[#6B7F3E]/40 text-[#2C3E2D]" : "bg-[#FAFAF5] border-[#E5E7EB] text-[#4A5D4E]"
                      }`
                    }
                  >
                    <Icon className="w-6 h-6 text-[#6B7F3E]" strokeWidth={1.5} />
                    {item.label}
                  </NavLink>
                );
              })}
              <a
                href="/"
                className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-[#E5E7EB] bg-[#FAFAF5] py-4 text-xs font-medium text-[#4A5D4E] active:scale-95 transition-transform"
              >
                <Home className="w-6 h-6 text-[#6B7F3E]" strokeWidth={1.5} />
                Website
              </a>
            </div>
            <div className="px-5 mt-4">
              <button
                onClick={() => { logout(); nav("/login"); }}
                data-testid="crm-mobile-logout-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-medium text-[#B85C38] bg-[#FDF2EA] active:scale-[0.98] transition-transform"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            </div>
          </div>
        </div>
      )}
      <ChatAssistant />
    </div>
  );
}
